import './_index.scss'

import { useMemo, useState, useEffect } from 'react'
import Button from '../../../Components/Buttons'
import { DocumentState, REF_STYLES } from '../../../redux/types'
import { useDispatch, useSelector } from 'react-redux'
import { selectDocumentState, setUserWriterSelections, setTitle } from '../../../redux/docSlice'
import { useDocId } from '../../../hooks/docID'
import { CONSTANTS, DOC_TEMPLATES, HUMANIZER_OPTIONS } from '../../../constants'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6'
import { IoIosRocket } from 'react-icons/io'
import { FEATURE_FLAGS, useFreeWordLimit } from '../../../hooks/featureFlags'
import WritingStyleSelection from '../../../Components/WriterOptions/WritingStyle'
import useIsPremium from '../../../hooks/useIsPremium'
import useWriteEssay from '../../../hooks/useWriteEssay'
import { useProcessedSubtopics } from '../hooks'
import { useLocalStorage } from '../../../helpers/utility'
import { blankUserWriterSelections } from '../../../redux/constants'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { SubtopicsInput } from '../Subtopics/SubtopicsInput'
import useNumSubtopics from '../../../hooks/numSubtopics'
import ReferencesSelection from '../References/ReferencesSelection'
import { logGoogleAdsEvent } from '../../../helpers/analytics'
import TitleStep from './TitleStep'
import { useQuery } from '../../../helpers/queryParams'
import { MdCheckCircle, MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md'
import useTrialEssayCredits from '../../../hooks/useTrialEssayCredits'
import TrialUpgrade from '../../../Components/Modals/TrialUpgrade'
import LoginModal from '../../../Components/Modals/Login'

const TopicStep = ({ docType }: { docType: keyof typeof DOC_TEMPLATES | undefined }) => {
	const docId = useDocId()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	if (!documentState) return null

	return (
		<div className="flex flex-col gap-4">
			<div className={`flex flex-col gap-2 mt-8 md:mt-18`}>
				<div className="text-3xl md:text-[34px] font-bold">What would you like to write about?</div>
				<div className="text-lg text-gray-700 ">
					Enter your topic and let Good AI craft your {docType === 'Outline' ? 'outline' : 'essay'}
				</div>
			</div>
			<TitleStep />
		</div>
	)
}

const WordCountStep = () => {
	const wordCountOptionsFF = useFeatureFlagVariantKey(FEATURE_FLAGS.WORD_COUNT_OPTIONS)
	const docId = useDocId()
	const dispatch = useDispatch()
	const freeWordLimit = useFreeWordLimit()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	const pageCount = useMemo(() => {
		const wordCount = documentState.userWriterSelections?.wordCount ?? freeWordLimit
		return (wordCount / 500).toFixed(1)
	}, [documentState.userWriterSelections?.wordCount])

	if (!documentState) return null

	const maxWordCount = CONSTANTS.WORD_COUNT_MAX

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-2">
				<div className={`text-3xl md:text-[34px] font-bold mt-8 md:mt-18`}>How long would you like it to be?</div>
				<div className="text-lg text-gray-700">Select your desired word count</div>
			</div>
			{wordCountOptionsFF === 'test' ? (
				<div className="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-3 mt-4">
					{[
						{ label: '1 page', range: '300-750', value: 400 },
						{ label: '2 pages', range: '750-1500', value: 1125 },
						{ label: '3 pages', range: '1500-2250', value: 1875 },
						{ label: '4+ pages', range: '2250-3000', value: 2625 },
					].map((option) => (
						<div
							key={option.label}
							className={`rounded-lg px-4 py-4 cursor-pointer transition-all border-2 text-center ${
								documentState.userWriterSelections?.wordCount === option.value
									? 'bg-gray-100 border-gray-600'
									: 'bg-white hover:bg-gray-50 border-gray-200'
							} shadow-sm`}
							onClick={() => {
								dispatch(
									setUserWriterSelections({
										docID: docId,
										userWriterSelections: { wordCount: option.value },
									})
								)
							}}
						>
							<div className="text-xl font-semibold text-gray-900 mb-1">{option.label}</div>
							<div className="text-md text-gray-700 mb-2">{option.range} words</div>
						</div>
					))}
				</div>
			) : (
				<div className="flex flex-col gap-5 text-center">
					<div className={`sliderValue mb-3 text-[40px] mt-4`}>
						<input
							onChange={(e) => {
								dispatch(
									setUserWriterSelections({
										docID: docId,
										userWriterSelections: { wordCount: parseInt(e.target.value) },
									})
								)
							}}
							onBlur={() => {
								const input = documentState.userWriterSelections?.wordCount ?? freeWordLimit
								if (input > maxWordCount) {
									dispatch(
										setUserWriterSelections({
											docID: docId,
											userWriterSelections: { wordCount: maxWordCount },
										})
									)
								} else if (input < CONSTANTS.WORD_COUNT_MIN) {
									dispatch(
										setUserWriterSelections({
											docID: docId,
											userWriterSelections: { wordCount: CONSTANTS.WORD_COUNT_MIN },
										})
									)
								}
							}}
							type="number"
							className={`value !w-[120px] text-center px-2 py-1 mr-1 font-bold `}
							value={documentState.userWriterSelections?.wordCount ?? freeWordLimit}
							placeholder=""
						/>
						<span className="text-2xl relative bottom-0.5 text-gray-700 font-medium">
							words
							<span className="md:inline hidden">, or ~{pageCount} pages</span>
						</span>
					</div>
					<div className="z-[1]">
						<input
							type="range"
							min={CONSTANTS.WORD_COUNT_MIN}
							max={maxWordCount}
							step={50}
							className="slider"
							value={documentState.userWriterSelections?.wordCount ?? freeWordLimit}
							onChange={(e) => {
								dispatch(
									setUserWriterSelections({
										docID: docId,
										userWriterSelections: { wordCount: parseInt(e.target.value) },
									})
								)
							}}
						/>
					</div>
					<div className="h-2 bg-gray-200 w-full relative -top-[35px] rounded-full">
						<div
							className="h-full bg-gray-900 rounded-full"
							style={{
								width: `${
									((documentState.userWriterSelections?.wordCount ?? freeWordLimit) / CONSTANTS.WORD_COUNT_MAX) * 100
								}%`,
							}}
						></div>
					</div>
				</div>
			)}
		</div>
	)
}

const EssayTypeStep = ({ docType }: { docType: keyof typeof DOC_TEMPLATES | undefined }) => {
	const docId = useDocId()
	const dispatch = useDispatch()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	const essayTypes = [
		{
			type: 'Classic',
			description: 'A traditional essay with a clear thesis statement and supporting evidence.',
			isDefault: true,
		},
		{
			type: 'Persuasive',
			description: 'An essay aimed at convincing readers of a specific viewpoint.',
		},
		{
			type: 'Personal Statement',
			description: 'An essay written to showcase goals and experiences for college admissions or scholarships.',
		},
		{
			type: 'Book Report',
			description: 'An essay that analyzes a book, its characters, and its themes.',
		},
		{
			type: 'Critique',
			description: 'A critical analysis of a work or concept.',
		},
		{
			type: 'Compare/Contrast',
			description: 'An essay that analyzes similarities and differences between subjects.',
		},
	]

	if (!documentState) return null

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-col gap-2">
				<div className={`text-3xl md:text-[34px] font-bold md:mt-18 mt-8`}>
					What type of essay {docType === 'Outline' && 'outline'} would you like?
				</div>
				<div className="text-lg text-gray-700">
					Select the type of {docType === 'Outline' ? 'outline' : 'essay'} you want to create
				</div>
			</div>
			<div className="grid md:grid-cols-2 grid-cols-1 gap-4">
				{essayTypes.map((essay, index) => (
					<div
						key={essay.type}
						className={`rounded-lg px-4 py-3 cursor-pointer transition-all border-2 ${
							essay.type === documentState.userWriterSelections?.type
								? 'bg-gray-100 border-gray-600'
								: 'bg-white hover:bg-gray-50 border-gray-200'
						} shadow-sm`}
						onClick={() => {
							dispatch(
								setUserWriterSelections({
									docID: docId,
									userWriterSelections: { type: essay.type },
								})
							)
						}}
					>
						<div className="flex items-center gap-2">
							<div className="text-lg font-bold">
								{essay.type} {essay.isDefault && '(default)'}
							</div>
						</div>
						<div className={`mt-2 `}>{essay.description}</div>
					</div>
				))}
			</div>
		</div>
	)
}

const WritingStyleStep = () => {
	const docId = useDocId()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))
	const personasFF = useFeatureFlagVariantKey(FEATURE_FLAGS.PERSONAS)

	if (!documentState) return null

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<div className={`text-3xl md:text-[34px] font-bold md:mt-18 mt-8`}>
					What writing {personasFF === 'test' ? 'persona' : 'style'} would you like?
				</div>
				<div className="text-lg text-gray-700">
					{personasFF === 'test'
						? 'Choose a writing persona or create a custom one'
						: 'Use AI to clone your writing style'}
				</div>
			</div>
			<WritingStyleSelection docID={docId} omitHeader />
		</div>
	)
}

const ReferencesStep = () => {
	const docId = useDocId()
	const dispatch = useDispatch()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	let options = [null, ...Object.keys(REF_STYLES)]

	if (!documentState) return null

	return (
		<>
			<div className="flex flex-col gap-10">
				<div className="flex flex-col gap-2">
					<div className={`text-3xl md:text-[34px] font-bold md:mt-18 mt-8`}>Need References?</div>
					<div className="text-lg text-gray-700">Select your reference style and choose your citations</div>
				</div>
				<div className="flex flex-col gap-8 mb-6">
					<div className={`grid gap-3 md:grid-cols-6 grid-cols-2`}>
						{options.map((str, index) => {
							return (
								<div
									className={`w-full text-lg rounded-lg py-1 font-medium text-center px-2 cursor-pointer border-2 ${
										str === documentState.userWriterSelections?.refStyle
											? 'bg-gray-100 border-gray-600'
											: 'bg-white hover:bg-gray-50 border-gray-200'
									} ${!str ? 'text-gray-700' : ''}`}
									key={(str ?? '') + index}
									onClick={(e) => {
										dispatch(
											setUserWriterSelections({
												docID: docId,
												userWriterSelections: { refStyle: str },
											})
										)
									}}
								>
									<span>
										{str
											? {
													None: 'None',
													APA_7: 'APA 7',
													MLA_9: 'MLA 9',
													HARVARD: 'Harvard',
													IEEE: 'IEEE',
													CHICAGO: 'Chicago',
											  }[str]
											: 'None'}
									</span>
								</div>
							)
						})}
					</div>
					<ReferencesSelection />
				</div>
			</div>
		</>
	)
}

const HumanizeStep = () => {
	const docId = useDocId()
	const dispatch = useDispatch()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	if (!documentState) return null

	return (
		<div className="flex flex-col gap-12">
			<div className="flex flex-col gap-2">
				<div className={`text-3xl md:text-[34px] font-bold md:mt-18 mt-8`}>
					What level of humanization would you like?
				</div>
				<div className="text-lg text-gray-700">
					The higher the level, the more natural and human-like your essay will sound, but it may affect readability.
				</div>
			</div>
			<div className="flex flex-col gap-2">
				<div className={`grid md:grid-cols-4 grid-cols-1 gap-3`}>
					{HUMANIZER_OPTIONS.map((str, index) => {
						return (
							<div
								className={`w-full text-lg rounded-lg py-5 font-medium text-center px-2 cursor-pointer border-2 ${
									str === documentState.userWriterSelections?.humanizerIntensity
										? 'bg-gray-100 border-gray-600'
										: 'bg-white hover:bg-gray-50 border-gray-200'
								}`}
								key={str + index}
								onClick={(e) => {
									dispatch(
										setUserWriterSelections({
											docID: docId,
											userWriterSelections: { humanizerIntensity: str },
										})
									)
								}}
							>
								<div className="flex flex-col gap-1">
									<span className="capitalize font-bold">{str.toLowerCase()}</span>
									<span className="text-gray-700 text-base">
										{
											{
												NONE: 'No humanization',
												LOW: 'Slightly variation',
												MEDIUM: 'Natural flow',
												HIGH: 'Most human-like',
											}[str]
										}
									</span>
								</div>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

const SubtopicsStep = () => {
	const docId = useDocId()
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))

	if (!documentState) return null

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-col gap-2">
				<div className={`text-3xl md:text-[34px] font-bold md:mt-18 mt-8`}>What are your subtopics?</div>
				<div className="text-lg text-gray-700">We've generated some subtopics for you based on your word count.</div>
			</div>
			<div className="flex flex-col gap-2 ">
				<SubtopicsInput docID={docId} />
			</div>
		</div>
	)
}

const SteppedEssaySetup = ({
	docType,
	setErrorModalOpen,
}: {
	docType: keyof typeof DOC_TEMPLATES | undefined
	setErrorModalOpen: (open: boolean) => void
}) => {
	const docID = useDocId()
	const [step, setStep] = useState(0)
	const [includeSubtopicsStep, setIncludeSubtopicsStep] = useState(false)
	const docId = useDocId()
	const isPremium = useIsPremium()
	const { isOnFreeTrial, hasCredits } = useTrialEssayCredits()
	const [trialUpgradeModalOpen, setTrialUpgradeModalOpen] = useState(false)
	const [trialLoginModalOpen, setTrialLoginModalOpen] = useState(false)
	const [trialLoading, setTrialLoading] = useState(false)
	const processedSubtopics = useProcessedSubtopics()
	const posthog = usePostHog()
	const { minNumberOfSubtopics } = useNumSubtopics()
	const { generateReferences } = useWriteEssay({ docID })
	const dispatch = useDispatch()
	const query = useQuery()

	const [, setLocalUserWriterSelections] = useLocalStorage(docId, blankUserWriterSelections)
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docId))
	const { generateEssay, getSubtopics } = useWriteEssay({
		docID: docId,
		type: docType,
		setLocalUserWriterSelections: (selections) => {
			setLocalUserWriterSelections(selections)
		},
		setErrorModalOpen: setErrorModalOpen,
	})

	// Handle title query parameter
	useEffect(() => {
		const titleFromQuery = query.get('title')
		if (titleFromQuery && documentState && (!documentState.title || documentState.title.length === 0)) {
			dispatch(setTitle({ docID: docId, title: decodeURIComponent(titleFromQuery) }))
			setStep(1)
		}
	}, [query, documentState, dispatch, docId])

	const steps = useMemo(() => {
		let baseSteps =
			docType === DOC_TEMPLATES.Outline
				? [
						{ id: 'topic', step: <TopicStep docType={docType} />, name: 'Topic' },
						{ id: 'essayType', step: <EssayTypeStep docType={docType} />, name: 'Essay Type' },
				  ]
				: [
						{ id: 'topic', step: <TopicStep docType={docType} />, name: 'Topic' },
						{ id: 'wordCount', step: <WordCountStep />, name: 'Word Count' },
						{ id: 'essayType', step: <EssayTypeStep docType={docType} />, name: 'Essay Type' },
						{ id: 'writingStyle', step: <WritingStyleStep />, name: 'Writing Style' },
						{ id: 'references', step: <ReferencesStep />, name: 'References' },
				  ]
		if (isPremium && docType !== DOC_TEMPLATES.Outline) {
			baseSteps.push({ id: 'humanize', step: <HumanizeStep />, name: 'Humanize' })
		}
		if (includeSubtopicsStep && docType !== DOC_TEMPLATES.Outline) {
			baseSteps.push({ id: 'subtopics', step: <SubtopicsStep />, name: 'Subtopics' })
		}
		return baseSteps
	}, [isPremium, includeSubtopicsStep, docType])

	const subtopicsStepDisabled = useMemo(() => {
		return processedSubtopics.filter((subtopic) => subtopic.trim().length > 0).length - 2 < minNumberOfSubtopics
	}, [processedSubtopics])

	// const disableNext = [!documentState?.title || documentState?.title?.length === 0, false, false, false, false]
	const disableNext = {
		topic: !documentState?.title || documentState?.title?.length === 0,
		wordCount: false,
		essayType: false,
		writingStyle: false,
		references: false,
		humanize: false,
		subtopics: subtopicsStepDisabled,
	}

	const ButtonWithArrow = ({ direction, children, ...props }: any) => {
		return (
			<Button
				{...props}
				className={`flex items-center group transition-all text-lg gap-5 ${
					direction === 'left' ? 'pr-7 pl-6' : 'pl-7 pr-6'
				}`}
			>
				{direction !== 'left' && children}
				<span
					className={`text-xl h-4 w-4 relative bottom-0.5 transition-all ${
						direction === 'left' ? 'group-hover:-left-1 left-0' : 'group-hover:right-0 right-1'
					}`}
				>
					{direction === 'left' ? FaArrowLeft({}) : FaArrowRight({})}
				</span>
				{direction === 'left' && children}
			</Button>
		)
	}

	const CurrentStepIndicator = ({ stepNumber }: { stepNumber: number }) => {
		if (stepNumber === step) {
			return MdRadioButtonChecked({ className: 'w-5 h-5' })
		}
		if (stepNumber < step) {
			return MdCheckCircle({ className: 'w-5 h-5' })
		}
		return MdRadioButtonUnchecked({ className: 'w-5 h-5 text-gray-500' })
	}

	const NavigationButtons = useMemo(() => {
		return (
			<div
				className={`flex justify-center gap-4 mx-auto mt-12 bg-white md:w-48 w-full fixed bottom-0 left-0 md:pb-0 py-2 md:static`}
			>
				{step !== 0 && (
					<div className="flex justify-end">
						<ButtonWithArrow type="ghost" onClick={() => setStep(Math.max(0, step - 1))} direction="left">
							Back
						</ButtonWithArrow>
					</div>
				)}
				{step === steps.length - 1 ? (
					<div>
						{
							<Button
								type="default"
								className="pl-8 pr-8 text-lg group"
								// @ts-ignore
								disabled={disableNext[steps[step].id]}
								onClick={() => {
									if (isOnFreeTrial && !hasCredits) {
										setTrialLoading(true)
										setTimeout(() => {
											setTrialLoading(false)
											setTrialUpgradeModalOpen(true)
										}, 500)
										return
									}
									generateEssay()
									posthog.capture('generate-essay', {
										word_count: documentState.userWriterSelections.wordCount,
										humanizer_intensity: documentState.userWriterSelections.humanizerIntensity,
										ref_style: documentState.userWriterSelections.refStyle,
										essay_type: documentState.userWriterSelections.type,
									})
									logGoogleAdsEvent('generateEssay')
								}}
							>
								<div className="flex items-center gap-2">
									{trialLoading ? 'Generating...' : 'Generate'}
									{!trialLoading && IoIosRocket({})}
								</div>
							</Button>
						}
					</div>
				) : (
					<div>
						<ButtonWithArrow
							type="default"
							// @ts-ignore
							disabled={disableNext[steps[step].id]}
							onClick={() => {
								posthog.capture('step-completed', { step: steps[step].id })
								setStep(Math.min(step + 1, steps.length - 1))
								if (steps[step].id === 'wordCount') {
									const generateSubtopics =
										documentState.userWriterSelections.wordCount >= CONSTANTS.TOPIC_GENERATION_BREAKPOINT
									setIncludeSubtopicsStep(generateSubtopics)
									if (generateSubtopics) {
										getSubtopics()
									}
									generateReferences()
								}
							}}
						>
							Next
						</ButtonWithArrow>
					</div>
				)}
			</div>
		)
	}, [
		step,
		steps,
		disableNext,
		generateEssay,
		posthog,
		documentState.userWriterSelections,
		setStep,
		getSubtopics,
		generateReferences,
		setIncludeSubtopicsStep,
		isOnFreeTrial,
		hasCredits,
		trialLoading,
	])

	return (
		<>
			<TrialUpgrade
				open={trialUpgradeModalOpen}
				setModalOpen={setTrialUpgradeModalOpen}
				openForPaymentRetries={true}
				outOfCredits={true}
				openForUndetectable={false}
			/>
			<LoginModal
				premiumPath={true}
				open={trialLoginModalOpen}
				setModalOpen={setTrialLoginModalOpen}
				annualPlan={false}
				context="trial-upgrade"
			/>
			<div
				className={`justify-between relative z-20 -m-2 lg:-top-[37px] -top-[6px] gap-2 hidden lg:flex`}
				style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
			>
				{steps.map((_, i) => (
					<>
						<div className="flex-col gap-2 w-full flex">
							<div
								className={`text-left ${
									i <= step ? 'text-gray-700 font-bold' : 'text-gray-600 font-medium '
								} text-sm flex items-center gap-2`}
							>
								<div
									className={`h-5 w-5 flex items-center justify-center bg-gray-200 rounded-full ${
										i <= step ? 'bg-gray-700 text-white' : ''
									}`}
								>
									{i + 1}
								</div>
								{steps[i].name}
							</div>
						</div>
					</>
				))}
			</div>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-4">{steps[step].step}</div>
				{NavigationButtons}
			</div>
		</>
	)
}

export default SteppedEssaySetup
